import { configureStore, combineReducers } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import { plotterSlice } from './plotter/plotterSlice';
import { pathsSlice } from './paths/pathsSlice';
import { selectedItemsSlice } from './selectedItems/selectedItemsSlice';
import { searchSlice } from './search/searchSlice';
import { overlaysSlice } from './overlays/overlaysSlice';
import { filtersSlice } from './filters/filtersSlice';
import { userSlice } from './user/userSlice';
import { edgeApi } from './services/edgeApi';
import { jsonBinApi } from './services/jsonBinApi';
import { pointsSlice } from './points/pointsSlice';
import { scenarioSlice } from './scenarios/scenarioSlice';
import { allPathsSlice } from './scenarios/allPaths/allPathsSlice';
import { iocSlice } from './ioc/iocSlice';
import { noticeSlice } from './notices/noticeSlice';
import { contractsSlice } from './contracts/contractsSlice';
import { mapSlice } from './map/slice/mapSlice';
import { advancedFiltersSlice } from './filters/advancedFiltersSlice';
import { pathFinderReducer } from './pathBuilder/pathFinderSlice';

import process from 'process';

// having issues with redux store persisting between tests so setting up a way to reset it to initial state each time
// https://stackoverflow.com/questions/59061161/how-to-reset-state-of-redux-store-when-using-configurestore-from-reduxjs-toolki

export type RootState = ReturnType<typeof combinedReducer>;

const combinedReducer = combineReducers({
  map: mapSlice.reducer,
  plotter: plotterSlice.reducer,
  paths: pathsSlice.reducer,
  selectedItems: selectedItemsSlice.reducer,
  search: searchSlice.reducer,
  points: pointsSlice.reducer,
  overlays: overlaysSlice.reducer,
  filters: filtersSlice.reducer,
  advancedFilters: advancedFiltersSlice.reducer,
  user: userSlice.reducer,
  notices: noticeSlice.reducer,
  contracts: contractsSlice.reducer,
  scenario: scenarioSlice.reducer,
  allPaths: allPathsSlice.reducer,
  ioc: iocSlice.reducer,
  pathFinder: pathFinderReducer,
  [edgeApi.reducerPath]: edgeApi.reducer,
  [jsonBinApi.reducerPath]: jsonBinApi.reducer,
});

const rootReducer = (
  state: ReturnType<typeof combinedReducer> = combinedReducer(undefined, {
    type: '',
  }),
  action: { type: string },
) => {
  if (action.type === 'plotter/resetStore') {
    state = {
      ...state,
      plotter: plotterSlice.getInitialState(),
      paths: pathsSlice.getInitialState(),
      selectedItems: selectedItemsSlice.getInitialState(),
      search: searchSlice.getInitialState(),
      points: pointsSlice.getInitialState(),
      overlays: overlaysSlice.getInitialState(),
      filters: filtersSlice.getInitialState(),
      advancedFilters: advancedFiltersSlice.getInitialState(),
      user: userSlice.getInitialState(),
      notices: noticeSlice.getInitialState(),
      contracts: contractsSlice.getInitialState(),
      scenario: scenarioSlice.getInitialState(),
      allPaths: allPathsSlice.getInitialState(),
      ioc: iocSlice.getInitialState(),
      map: mapSlice.getInitialState(),
      pathFinder: { results: [], avoidConstraints: [] },
    };
  }
  return combinedReducer(state, action);
};

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({ serializableCheck: false }).concat(
      edgeApi.middleware,
    ),
});

if (process.env.NODE_ENV !== 'production') {
  // assign the store to the window object
  (window as any).store = store;
}

setupListeners(store.dispatch);
